import type { PaymentMethod } from '../api/shipping-addresses'
import { daysUntil, formatMonthYear } from './dates'

/** Card brands as Recharge spells them, and as a parent reads them. */
const BRANDS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  master: 'Mastercard',
  amex: 'American Express',
  american_express: 'American Express',
  discover: 'Discover',
  diners_club: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
}

/** Wallets by payment type; a wallet is named rather than described by its card. */
const WALLETS: Record<string, string> = {
  PAYPAL: 'PayPal',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  SHOP_PAY: 'Shop Pay',
  VENMO: 'Venmo',
}

/** How long before a card's expiry the row says so, in days. */
const EXPIRY_NOTICE_DAYS = 30

/** "Visa ending in 1234", "PayPal", or whatever Recharge called something it is neither. */
export function paymentMethodLabel(method: PaymentMethod): string {
  const wallet = WALLETS[method.type]
  if (wallet) return wallet

  const brand = method.brand
    ? (BRANDS[method.brand.toLowerCase()] ?? capitalise(method.brand))
    : 'Card'
  return method.last4 ? `${brand} ending in ${method.last4}` : brand
}

/** "12/2027", for a method that expires. */
export function paymentMethodExpiry(method: PaymentMethod): string | null {
  return method.expiresOn ? formatMonthYear(method.expiresOn) : null
}

/**
 * Why the method needs attention, or null. Recharge's own verdict first —
 * a card it could not charge — then the card's expiry, which Recharge does
 * not report ahead of time.
 */
export function paymentMethodWarning(method: PaymentMethod, today = new Date()): string | null {
  if (method.status === 'invalid') return 'This card can’t be charged.'
  if (!method.expiresOn) return null

  const days = daysUntil(method.expiresOn, today)
  if (days < 0) return 'This card has expired.'
  if (days <= EXPIRY_NOTICE_DAYS) return 'This card expires soon.'
  return null
}

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
}
